import type { OrgMember, OrgRole, UserOrganization } from '@outpost/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, KeyRound, LogOut, ShieldCheck, Trash2, Users } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { EmptyState, ErrorState, ListSkeleton } from '@/components/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useAuth } from '@/lib/auth';
import { allows, canChangeRole, canRemove, ROLES, useOrganizations } from '@/lib/roles';
import { AllowedDomains } from '@/pages/organization-allowed-domains';
import { Integrations } from '@/pages/organization-integrations';
import { Invitations } from '@/pages/organization-invitations';
import { StatsTokens } from '@/pages/organization-stats-tokens';
import { Teams } from '@/pages/organization-teams';
import { UploadTokens } from '@/pages/organization-upload-tokens';

export function OrganizationSettingsPage() {
  const { orgId = '' } = useParams();
  const organizations = useOrganizations();
  const organization = organizations.data?.find((org) => org.id === orgId);

  if (organizations.isLoading) return <ListSkeleton rows={3} />;
  if (organizations.isError) {
    return <ErrorState error={organizations.error} onRetry={() => void organizations.refetch()} />;
  }
  if (!organization) {
    return (
      <EmptyState
        icon={<Building2 className="size-5" />}
        title="Organization not found"
        description="It may not exist, or you may no longer be a member of it."
      />
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold">{organization.name} settings</h1>
        <p className="text-sm text-muted-foreground">
          The organization&apos;s name, who belongs to it, how they are grouped, where its alerts
          can go, the services that read its error counts, and your tokens for uploading source maps
          to it.
        </p>
      </div>

      <General organization={organization} />
      <TwoFactor organization={organization} />
      <SingleSignOn organization={organization} />
      <AllowedDomains organization={organization} />
      <Integrations organization={organization} />
      <Members organization={organization} />
      <Teams organization={organization} />
      {allows(organization.role, 'admin') && <Invitations organization={organization} />}
      {allows(organization.role, 'admin') && <StatsTokens organization={organization} />}
      <UploadTokens organization={organization} />
    </div>
  );
}

function General({ organization }: { organization: UserOrganization }) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const [name, setName] = useState(organization.name);
  const canEdit = allows(organization.role, 'admin');

  const rename = useMutation({
    mutationFn: (next: string) => client.updateOrganization(organization.id, { name: next }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['organizations'] }),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Building2 className="size-4" />
          General
        </CardTitle>
        <CardDescription>
          The slug is fixed. AI assistants connected over MCP refer to projects by it, so renaming
          the organization does not change it.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor="org-name">Name</Label>
          <div className="flex gap-2">
            <Input
              id="org-name"
              className="max-w-sm"
              value={name}
              disabled={!canEdit}
              onChange={(event) => setName(event.target.value)}
            />
            {canEdit && (
              <Button
                variant="outline"
                onClick={() => rename.mutate(name)}
                disabled={!name.trim() || name.trim() === organization.name || rename.isPending}
              >
                Save
              </Button>
            )}
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>Slug</Label>
          <code className="block text-sm text-muted-foreground">{organization.slug}</code>
        </div>
        {rename.error && (
          <p className="text-sm text-destructive">{(rename.error as Error).message}</p>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Whether everyone must reach this organization through a second factor.
 *
 * Owners only, and only from a session that already satisfies it: turning it on
 * from a password-only session would lock the owner out of the organization —
 * including out of this setting.
 */
function TwoFactor({ organization }: { organization: UserOrganization }) {
  const { client, mfa } = useAuth();
  const queryClient = useQueryClient();
  const isOwner = organization.role === 'owner';

  const setRequired = useMutation({
    mutationFn: (require_mfa: boolean) =>
      client.updateOrganization(organization.id, { require_mfa }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['organizations'] });
      // What a member can see changes with it.
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });

  // Shows where the switch is going while the change is in flight, rather than
  // snapping back until the organizations query refetches.
  const required = setRequired.isPending ? setRequired.variables : organization.require_mfa;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <ShieldCheck className="size-4" />
          Two-factor authentication
        </CardTitle>
        <CardDescription>
          When this is on, members reach {organization.name} only from a sign-in that used an
          authenticator code or a passkey. A passkey counts on its own and is never asked for a
          code. Members without either keep their account and see this organization&apos;s projects
          again as soon as they set one up.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-3">
          <Label htmlFor="require-mfa" className="min-w-0 flex-1">
            Require for everyone
          </Label>
          <Switch
            id="require-mfa"
            checked={required}
            onCheckedChange={(checked) => setRequired.mutate(checked)}
            disabled={!isOwner || setRequired.isPending || (!required && !mfa)}
            title={
              isOwner && !required && !mfa
                ? 'Set up an authenticator app or a passkey for yourself first'
                : undefined
            }
          />
        </div>

        {isOwner && !required && !mfa && (
          <p className="text-sm text-muted-foreground">
            You are signed in with only a password. Add an authenticator app or a passkey in{' '}
            <Link to="/account" className="underline underline-offset-4">
              your user settings
            </Link>{' '}
            first, or turning this on would lock you out.
          </p>
        )}
        {!isOwner && (
          <p className="text-sm text-muted-foreground">Only an owner can change this.</p>
        )}
        {setRequired.error && (
          <p className="text-sm text-destructive">{(setRequired.error as Error).message}</p>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Which identity providers this organization accepts, and whether it accepts
 * anything else.
 *
 * The providers themselves are a property of the deployment — an administrator
 * supplies a client id and secret as environment variables — so this lists what
 * is available and lets the organization opt in to each. Requiring single
 * sign-on only appears once at least one is enabled, because an organization
 * that required it with none would refuse every session there is, including the
 * owner's.
 */
function SingleSignOn({ organization }: { organization: UserOrganization }) {
  const { client, sso } = useAuth();
  const queryClient = useQueryClient();
  const isOwner = organization.role === 'owner';
  const canEdit = allows(organization.role, 'admin');

  const settings = useQuery({
    queryKey: ['org-sso', organization.id],
    queryFn: () => client.organizationSso(organization.id),
  });

  const changed = () => {
    void queryClient.invalidateQueries({ queryKey: ['org-sso', organization.id] });
    void queryClient.invalidateQueries({ queryKey: ['organizations'] });
    // What a member can see changes with it.
    void queryClient.invalidateQueries({ queryKey: ['projects'] });
  };

  const setEnabled = useMutation({
    mutationFn: ({ provider, enabled }: { provider: string; enabled: boolean }) =>
      client.setOrganizationSsoProvider(organization.id, provider, enabled),
    onSuccess: changed,
  });

  const setRequired = useMutation({
    mutationFn: (require_sso: boolean) =>
      client.updateOrganization(organization.id, { require_sso }),
    onSuccess: changed,
  });

  // Both show where a switch is going while its change is in flight, rather
  // than snapping back until the queries refetch.
  const providers = (settings.data?.providers ?? []).map((provider) =>
    setEnabled.isPending && setEnabled.variables.provider === provider.id
      ? { ...provider, enabled: setEnabled.variables.enabled }
      : provider,
  );
  const enabled = providers.filter((provider) => provider.enabled);
  const required = setRequired.isPending ? setRequired.variables : organization.require_sso;
  // The server refuses a session established anywhere else, so letting an owner
  // who would be locked out by it flip the switch helps nobody.
  const wouldLockMeOut = !enabled.some((provider) => provider.id === sso);
  const error = setEnabled.error ?? setRequired.error;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <KeyRound className="size-4" />
          Single sign-on
        </CardTitle>
        <CardDescription>
          Members can sign in to {organization.name} with any provider enabled here. Providers
          appear once an administrator has configured their credentials on this Outpost
          installation.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {settings.isLoading && <ListSkeleton rows={2} />}
        {settings.isError && (
          <ErrorState error={settings.error} onRetry={() => void settings.refetch()} />
        )}

        {settings.isSuccess && providers.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No identity providers are configured on this installation. An administrator can add
            Google or Microsoft credentials to the server&apos;s environment to offer them here.
          </p>
        )}

        {providers.map((provider) => (
          <div
            key={provider.id}
            className="flex items-center gap-3 rounded-md border border-border px-3 py-2"
          >
            <Label htmlFor={`sso-provider-${provider.id}`} className="min-w-0 flex-1">
              {provider.name}
            </Label>
            <Switch
              id={`sso-provider-${provider.id}`}
              checked={provider.enabled}
              onCheckedChange={(checked) =>
                setEnabled.mutate({ provider: provider.id, enabled: checked })
              }
              disabled={!canEdit || setEnabled.isPending}
            />
          </div>
        ))}

        {enabled.length > 0 && (
          <div className="space-y-3 rounded-md border border-border px-3 py-3">
            <div className="flex items-center gap-3">
              <Label htmlFor="require-sso" className="min-w-0 flex-1">
                Require single sign-on
              </Label>
              <Switch
                id="require-sso"
                checked={required}
                onCheckedChange={(checked) => setRequired.mutate(checked)}
                disabled={!isOwner || setRequired.isPending || (!required && wouldLockMeOut)}
                title={
                  isOwner && !required && wouldLockMeOut
                    ? 'Sign in with one of these providers first'
                    : undefined
                }
              />
            </div>
            <p className="text-sm text-muted-foreground">
              When this is on, members reach {organization.name} only from a sign-in that used one
              of the providers above. Email and password, authenticator codes and passkeys stop
              working for this organization; they keep working everywhere else, and members keep
              their accounts.
            </p>
            {isOwner && !required && wouldLockMeOut && (
              <p className="text-sm text-muted-foreground">
                You are signed in another way. Sign out and sign in again with one of these
                providers first, or turning this on would lock you out.
              </p>
            )}
            {!isOwner && (
              <p className="text-sm text-muted-foreground">Only an owner can change this.</p>
            )}
          </div>
        )}

        {error && <p className="text-sm text-destructive">{(error as Error).message}</p>}
      </CardContent>
    </Card>
  );
}

function Members({ organization }: { organization: UserOrganization }) {
  const { client, user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const actor = organization.role;

  const members = useQuery({
    queryKey: ['org-members', organization.id],
    queryFn: () => client.listMembers(organization.id),
  });

  const changed = () => {
    void queryClient.invalidateQueries({ queryKey: ['org-members', organization.id] });
    // A role change can change the caller's own role, and who sees which
    // project.
    void queryClient.invalidateQueries({ queryKey: ['organizations'] });
    void queryClient.invalidateQueries({ queryKey: ['project-members'] });
  };

  const setRole = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: OrgRole }) =>
      client.updateMemberRole(organization.id, userId, role),
    onSuccess: changed,
  });
  const remove = useMutation({
    mutationFn: (userId: string) => client.removeMember(organization.id, userId),
    onSuccess: (_, userId) => {
      if (userId === user?.id) {
        void queryClient.invalidateQueries({ queryKey: ['organizations'] });
        void queryClient.invalidateQueries({ queryKey: ['projects'] });
        navigate('/', { replace: true });
      } else {
        changed();
      }
    },
  });

  const error = setRole.error ?? remove.error;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Users className="size-4" />
          Members
        </CardTitle>
        <CardDescription>
          Owners and admins can access every project. Members see the projects they are added to
          under each project&apos;s settings, plus the projects of the teams they are on.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {members.isLoading && <ListSkeleton rows={3} />}
        {members.isError && (
          <ErrorState error={members.error} onRetry={() => void members.refetch()} />
        )}

        {members.data?.map((member) => (
          <MemberRow
            key={member.id}
            member={member}
            actor={actor}
            isSelf={member.id === user?.id}
            busy={setRole.isPending || remove.isPending}
            onRoleChange={(role) => setRole.mutate({ userId: member.id, role })}
            onRemove={() => {
              const leaving = member.id === user?.id;
              const question = leaving
                ? `Leave ${organization.name}? You will lose access to all of its projects.`
                : `Remove ${member.name} from ${organization.name}?`;
              if (window.confirm(question)) remove.mutate(member.id);
            }}
          />
        ))}

        {error && <p className="text-sm text-destructive">{(error as Error).message}</p>}
      </CardContent>
    </Card>
  );
}

function MemberRow({
  member,
  actor,
  isSelf,
  busy,
  onRoleChange,
  onRemove,
}: {
  member: OrgMember;
  actor: OrgRole;
  isSelf: boolean;
  busy: boolean;
  onRoleChange: (role: OrgRole) => void;
  onRemove: () => void;
}) {
  // Offer only the roles the server would accept, plus the current one.
  const options = ROLES.filter(
    (role) => role === member.role || canChangeRole(actor, member.role, role),
  );
  const removable = canRemove(actor, member.role, isSelf);

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-md border border-border px-3 py-2">
      {/* On a phone the controls drop below, rather than leaving the name no room. */}
      <div className="w-full min-w-0 sm:w-auto sm:flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium">{member.name}</span>
          {isSelf && <Badge variant="muted">you</Badge>}
        </div>
        <span className="block truncate text-xs text-muted-foreground">{member.email}</span>
      </div>

      {options.length > 1 ? (
        <Select
          value={member.role}
          onValueChange={(role) => onRoleChange(role as OrgRole)}
          disabled={busy}
        >
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {options.map((role) => (
              <SelectItem key={role} value={role}>
                {role}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <Badge variant="secondary">{member.role}</Badge>
      )}

      {removable &&
        (isSelf ? (
          <Button variant="outline" size="sm" onClick={onRemove} disabled={busy}>
            <LogOut />
            Leave
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="icon"
            title={`Remove ${member.name}`}
            onClick={onRemove}
            disabled={busy}
          >
            <Trash2 />
          </Button>
        ))}
    </div>
  );
}
